import { useEffect } from 'react';
import type { Booking } from '@/types';

/** Only schedule same-day unlocks; anything later is picked up by normal refetches. */
const MAX_WAIT_MS = 24 * 60 * 60 * 1000;
/** Small buffer so the server clock has definitely passed the unlock time. */
const BUFFER_MS = 5_000;
/** If a refetch at the unlock time still says locked (clock skew), try again after this. */
const RETRY_MS = 30_000;

/**
 * Refetches once the earliest locked booking's contacts unlock, so an open page
 * flips from "unlocks at…" to the phone/WhatsApp buttons without a manual reload.
 * `dataUpdatedAt` re-arms the timer after every fetch, so a refetch that is still
 * locked (client clock ahead of the server) schedules a retry instead of giving up.
 */
export function useRefetchAtContactUnlock(
  bookings: Array<Pick<Booking, 'contactAccess'>> | undefined,
  refetch: () => unknown,
  dataUpdatedAt: number
) {
  const nextUnlock = (bookings ?? [])
    .filter((b) => b.contactAccess?.state === 'locked' && b.contactAccess.unlocksAt)
    .map((b) => new Date(b.contactAccess!.unlocksAt!).getTime())
    .filter((t) => Number.isFinite(t))
    .sort((a, b) => a - b)[0];

  useEffect(() => {
    if (nextUnlock == null) return;
    const delay = nextUnlock - Date.now() + BUFFER_MS;
    if (delay > MAX_WAIT_MS) return;
    const timer = window.setTimeout(() => void refetch(), delay > 0 ? delay : RETRY_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch is stable; re-arm on new target or new data
  }, [nextUnlock, dataUpdatedAt]);
}
